// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullAdminRegatta,
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  RegattaRequirementType,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  shortenText,
} from 'common.helpers/dist/browser';
import {
  regattaRequirementTypeLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';

// Components
import EmptyTable from '../../../common/EmptyTable';
import RegattaRequirementFields from '../../../fields/RegattaRequirementFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
type $Props = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

type $State = {
  data: Array<$Model<'RegattaRequirement'>>;
  loading: boolean;
};

class RegattaRequirementsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaRequirement') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaRequirement({
          RegattaId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    const RegattaId = item.id;

    const newItem: $ModelUpsertParams<'RegattaRequirement'> = {
      Content: '',
      Name: '',
      RegattaId,
      Required: true,
      Source: null,
      Type: RegattaRequirementType.value,
    };

    return (
      <SectionHeader
        bottomBorder
        description={t('A list of values / files asked to input by the regatta entries on the regatta registration form')}
        iconClass={'icon-list'}
        rightButtons={(
          <FormDialog
            fields={(
              <RegattaRequirementFields item={newItem} />
            )}
            id={'create-regatta-requirement-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'RegattaRequirement'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertRegattaRequirement(params);
              },
            })}
            subTitle={t('New')}
            title={t('Regatta requirement')}
          >
            <Button
              color={'info'}
              id={'create-regatta-requirement-button'}
              size={'sm'}
              title={t('Add regatta requirement')}
            >
              {t('Add new')}
            </Button>
          </FormDialog>
        )}
        title={t('Regatta requirements')}
      />
    );
  }

  renderButtons(dataItem: $Model<'RegattaRequirement'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <FormDialog
          fields={(
            <RegattaRequirementFields item={dataItem} />
          )}
          id={`update-regatta-requirement-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaRequirement'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertRegattaRequirement(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regatta registration requirement')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-requirement-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-requirement-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaRequirement(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta registration requirement')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-requirement-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th className={'py-1'}>{t('Description')}</th>
            <th className={'py-1'}>{t('Type')}</th>
            <th className={'text-center py-1'}>{t('Required')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaRequirement'>) => (
            <tr key={dataItem.id}>
              <td className={'py-1'}>
                <div>
                  {dataItem.Name}
                </div>
                <small className={'text-muted'}>
                  {shortenText(
                    dataItem.Content,
                    64,
                  )}
                </small>
              </td>
              <td className={'py-1'}>{t(regattaRequirementTypeLabel[dataItem.Type])}</td>
              <td className={'text-center'}>
                {dataItem.Required === true && (
                  <em className={'icon-check'} />
                )}
              </td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default RegattaRequirementsSectionView;
